import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useRef } from 'react';
import { Menu, X, LogOut, PlusCircle, Users, Bell, Calendar, UserPlus, Compass } from 'react-feather';
import { API_URL, fetchWithAuth, getImageUrl } from '../api';
import { FaTicketAlt } from 'react-icons/fa';

export default function Navigation() {
  const { user, logout, friendRequests = [], eventRequests = [], fetchNotifications } = useAuth();
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const navigate = useNavigate();
  const notifRef = useRef(null);

  const validEventRequests = eventRequests.filter(req => {
    const myRole = req.event?.current_user_role || req.my_role;
    return myRole === 'Socio' || myRole === 'CREADOR DEL EVENTO' || user?.role === 'admin';
  })

  const totalNotifications = (friendRequests?.length || 0) + (validEventRequests?.length || 0);

  // Removes Vertical Scroll.
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Open Close Notifications Panel.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Closes menu, Logs out and Navigates Home.
  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  // Notification panel interactions.
  const handleAcceptRequest = async (senderId) => {
    try {
      await fetchWithAuth(`${API_URL}/friends/accept/${senderId}`, { method: 'POST' });
      fetchNotifications();
    } catch (err) { console.error(err); }
  };

  const handleRejectRequest = async (senderId) => {
    try {
      await fetchWithAuth(`${API_URL}/friends/reject/${senderId}`, { method: 'POST' });
      fetchNotifications();
    } catch (err) { console.error(err); }
  };

  const handleEventRequestClick = (eventId) => {
    setNotificationOpen(false);
    navigate(`/event/${eventId}`); 
  };

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b border-white/5 
      ${scrolled || menuOpen || notificationOpen ? 'bg-black/80 backdrop-blur-md py-3' : 'bg-transparent py-5'}`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          
          {/* 1. LOGO */}
          <Link to="/" className="flex items-center gap-2 group z-60" onClick={() => setMenuOpen(false)}>
            <img 
              src="/icons/white goclub.ico" 
              alt="Logo" 
              className="h-8 w-8 group-hover:scale-110 transition-transform duration-300"
            />
            <span className="text-xl font-bold tracking-wider text-white">AFTR</span>
          </Link>


          {/* 2. DESKTOP NAVIGATION */}
          <div className="hidden md:flex items-center gap-8">
            {/* IF ROLE == USER */}
            {!user || (user.role !== 'guard') ? (
              <>
                <Link to="/explore" className="text-gray-300 hover:text-white text-sm font-medium transition-colors flex items-center gap-2">
                  <Compass size={16}/> EVENTOS
                </Link>
                {user && (
                    <>
                    {/* --- NUEVO: ACCESO DIRECTO A ENTRADAS --- */}
                    <Link to="/passes" className="text-yellow-400 hover:text-yellow-200 text-sm font-bold transition-colors flex items-center gap-2">
                        <FaTicketAlt size={16}/> QRs
                    </Link>

                    <Link to="/friends" className="text-gray-300 hover:text-white text-sm font-medium transition-colors flex items-center gap-2">
                        <Users size={16}/> GRUPOS
                    </Link>
                    <Link to="/create" className="text-gray-300 hover:text-white text-sm font-medium transition-colors flex items-center gap-1">
                        <PlusCircle size={16}/> CREAR
                    </Link>
                    </>
                )}
              </>
            ) : null}

            {/* IF ROLE == ADMIN */}
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors">
                ADMIN
              </Link>
            )}
          </div>


          {/* 3. RIGHT SIDE */}
          <div className="flex items-center gap-4">
            
            {user ? (
              <>
                {/* --- NOTIFICATION BELL --- */}
                <div className="relative" ref={notifRef}>
                  <button 
                    onClick={() => setNotificationOpen(!notificationOpen)}
                    className={`p-2 rounded-full transition-colors relative ${notificationOpen ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                  >
                    <Bell size={20} />
                    {totalNotifications > 0 && (
                      <span className="absolute top-1.5 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-black animate-pulse"></span>
                    )}
                  </button>

                  {/* FLOATING DROPDOWN */}
                  {notificationOpen && (
                    <div className="absolute top-full right-[-60px] md:right-0 mt-3 w-80 md:w-96 bg-[#121212] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-fade-in z-50">
                       <div className="p-4 border-b border-white/5 bg-white/5 backdrop-blur-md flex justify-between items-center">
                          <h3 className="text-sm font-bold text-white">Notificaciones</h3>
                          {totalNotifications > 0 && (
                            <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">{totalNotifications}</span>
                          )}
                       </div>
                       
                       <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                          {totalNotifications === 0 ? (
                            <div className="p-8 text-center text-gray-500 text-sm">
                               <p>No tienes notificaciones nuevas.</p>
                            </div>
                          ) : (
                            <>
                                {/* Event Requests */}
                                {validEventRequests?.length > 0 && (
                                    <div className="border-b border-white/5">
                                        <div className="px-4 py-2 bg-white/[0.02] text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                            <Calendar size={12}/> Solicitudes de Eventos
                                        </div>
                                        {validEventRequests.map((req, idx) => (
                                            <div key={`evt-${idx}`} className="p-4 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3">
                                                <div className="flex-1">
                                                    <p className="text-sm text-white"><span className="font-bold">{req.user?.name}</span> solicitó entrar a:</p>
                                                    <p className="text-xs text-purple-300 font-medium mb-2">{req.event?.name}</p>
                                                    <button onClick={() => handleEventRequestClick(req.event_id)} className="w-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold py-1.5 rounded-lg border border-white/10">Gestionar Solicitud</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Friend Requests */}
                                {friendRequests?.length > 0 && (
                                    <div>
                                        <div className="px-4 py-2 bg-white/[0.02] text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                            <UserPlus size={12}/> Solicitudes de Amistad
                                        </div>
                                        {friendRequests.map((req) => (
                                           <div key={req.sender_id} className="p-4 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3">
                                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-sm font-bold text-white shrink-0">{req.sender.name.charAt(0)}</div>
                                              <div className="flex-1">
                                                  <p className="text-sm text-white font-medium">{req.sender.name}</p>
                                                  <div className="flex gap-2 mt-2">
                                                      <button onClick={() => handleAcceptRequest(req.sender_id)} className="flex-1 bg-white text-black text-xs font-bold py-1.5 rounded-lg">Aceptar</button>
                                                      <button onClick={() => handleRejectRequest(req.sender_id)} className="flex-1 bg-white/5 text-gray-300 text-xs font-bold py-1.5 rounded-lg">Rechazar</button>
                                                  </div>
                                              </div>
                                           </div>
                                        ))}
                                    </div>
                                )}
                            </>
                          )}
                       </div>
                    </div>
                  )}
                </div>

                {/* --- PROFILE --- */}
                  <div className="hidden md:flex items-center gap-4">
                    <Link to={`/profile/${user.id}`} className="flex items-center gap-3 pl-1 pr-4 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/5 transition-all group">
                      
                      {user.profile_image_url ? (
                        <img 
                          src={getImageUrl(user.profile_image_url)} 
                          alt="Profile" 
                          className="w-8 h-8 rounded-full object-cover shadow-lg border border-white/10"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-xs font-bold text-white shadow-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}

                      <span className="text-sm font-medium text-white group-hover:text-white/90 max-w-[100px] truncate">
                          {user.name.split(' ')[0]}
                      </span>
                    </Link>
                    
                    <button onClick={handleLogout} className="p-2 text-gray-400 hover:text-red-400 transition-colors">
                      <LogOut size={20} />
                    </button>
                  </div>
              </>
            ) : (
                <div className="hidden md:flex items-center gap-4">
                    <Link to="/login" className="text-sm font-medium text-white hover:text-gray-300 transition-colors">LOGIN</Link>
                    <Link to="/register" className="px-4 py-2 rounded-lg bg-white text-black text-sm font-bold hover:bg-gray-200 transition-colors">REGISTRO</Link>
                </div>
            )}

            {/* MOBILE TOGGLE ICON */}
            <button className="md:hidden p-2 text-white z-50 relative" onClick={() => setMenuOpen(!menuOpen)}>
               {!menuOpen && <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
        
      {/* MOBILE MENU - VERSIÓN MEJORADA */}
      {menuOpen && (
        <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm md:hidden transition-opacity duration-200 animate-fade-in"
          aria-hidden="true"
        />

        <div className="fixed inset-0 z-[100] bg-black md:hidden animate-slideUp">
          {/* Header del menú móvil */}
          <div className="sticky top-0 bg-black border-b border-white/20 px-4 py-4 flex justify-between items-center z-50">
            <Link to="/" className="flex items-center gap-2" onClick={() => setMenuOpen(false)}>
              <img src="/icons/white goclub.ico" alt="Logo" className="h-7 w-7" />
              <span className="text-lg font-bold text-white">AFTR</span>
            </Link>
            <button 
              onClick={() => setMenuOpen(false)} 
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Contenido scrolleable */}
          <div className="overflow-y-auto pb-20">
            <div className="px-4 py-6 bg-black">
              {user ? (
                /* VISTA USUARIO LOGUEADO */
                <div className="space-y-4">
                  {/* Perfil del usuario */}
                  <Link 
                    to={`/profile/${user.id}`}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-4 p-4 bg-white/5 border border-white/20 rounded-2xl active:scale-[0.98] transition-all hover:bg-white/10 hover:border-white/40"
                  >
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-xl font-bold text-white shrink-0 shadow-lg">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-white truncate">{user.name}</h3>
                      <p className="text-xs text-purple-300">Ver perfil completo</p>
                    </div>
                  </Link>

                  {/* Divisor */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                  {/* Menú de navegación */}
                  <div className="space-y-3">
                    {user.role !== 'guard' && (
                      <>
                        {/* --- NUEVO: ACCESO PRIORITARIO A ENTRADAS --- */}
                        <Link 
                          to="/passes" 
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 p-4 rounded-xl bg-yellow-900/10 hover:bg-yellow-900/20 active:bg-yellow-900/30 transition-all group border border-yellow-500/20 hover:border-yellow-500/40"
                        >
                          <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0 group-hover:bg-yellow-500/30 transition-colors">
                            <FaTicketAlt size={20} className="text-yellow-400"/>
                          </div>
                          <span className="text-sm font-bold text-yellow-100">Mis Entradas (QR)</span>
                        </Link>

                        <Link 
                          to="/explore" 
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/15 transition-all group border border-white/20 hover:border-white/40"
                        >
                          <div className="w-10 h-10 rounded-full bg-purple-600/30 flex items-center justify-center shrink-0 group-hover:bg-purple-600/40 transition-colors">
                            <Compass size={20} className="text-purple-300"/>
                          </div>
                          <span className="text-sm font-semibold text-white">Explorar Eventos</span>
                        </Link>

                        <Link 
                          to="/friends" 
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/15 transition-all group border border-white/20 hover:border-white/40"
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-600/30 flex items-center justify-center shrink-0 group-hover:bg-blue-600/40 transition-colors">
                            <Users size={20} className="text-blue-300"/>
                          </div>
                          <span className="text-sm font-semibold text-white">Mi Red Social</span>
                        </Link>

                        <Link 
                          to="/create" 
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 active:bg-white/15 transition-all group border border-white/20 hover:border-white/40"
                        >
                          <div className="w-10 h-10 rounded-full bg-green-600/30 flex items-center justify-center shrink-0 group-hover:bg-green-600/40 transition-colors">
                            <PlusCircle size={20} className="text-green-300"/>
                          </div>
                          <span className="text-sm font-semibold text-white">Crear Evento</span>
                        </Link>
                      </>
                    )}
                  </div>

                  {/* Divisor */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                  {/* Cerrar sesión */}
                  <button 
                    onClick={handleLogout}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-red-600/20 active:bg-red-600/30 transition-all w-full group border border-white/20 hover:border-red-500/50"
                  >
                    <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center shrink-0 group-hover:bg-red-600/30 transition-colors">
                      <LogOut size={20} className="text-red-400"/>
                    </div>
                    <span className="text-sm font-semibold text-red-300">Cerrar Sesión</span>
                  </button>
                </div>
              ) : (
                /* VISTA USUARIO NO LOGUEADO */
                <div className="space-y-3">
                  <Link 
                    to="/login" 
                    onClick={() => setMenuOpen(false)}
                    className="block w-full py-4 px-4 text-center rounded-xl bg-white/10 border border-white/30 text-white font-bold text-sm hover:bg-white/20 hover:border-white/50 active:scale-[0.98] transition-all"
                  >
                    Iniciar Sesión
                  </Link>
                  
                  <Link 
                    to="/register" 
                    onClick={() => setMenuOpen(false)}
                    className="block w-full py-4 px-4 text-center rounded-xl bg-white text-black font-bold text-sm hover:bg-gray-100 active:scale-[0.98] transition-all"
                  >
                    Registrarse
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slideUp { animation: slideUp 0.2s ease-out forwards; }
          .animate-fade-in { animation: fadeIn 0.2s ease-out forwards; }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        </>
      )}
    </nav>
  );
}